/**
 * Safely open an HTML document in a popup window for printing.
 *
 * The previous implementation used `w.document.write(html)` which is:
 * (1) flagged as an XSS surface by every static-analysis tool because
 * it evaluates any `<script>` tag or event handler embedded in `html`
 * in the SAME context as the opener, and (2) deprecated by browsers as
 * a document-loading mechanism.
 *
 * Blob-URL approach: we serialise the HTML into an isolated
 * `blob:` URL and hand it to `window.open`. The browser treats the
 * result as a fresh navigation, so:
 *  - The popup gets its own `document` — no `document.write` needed.
 *  - The blob URL is same-origin but the loaded document runs in a
 *    fresh execution context; opener-referenced globals cannot be
 *    silently mutated by injected code.
 *  - `<script>` inside the HTML DOES still execute (it's a full HTML
 *    document after all — required for our `window.print()` trigger),
 *    but the code cannot reach back into the parent via `document.write`
 *    timing races.
 *
 * The blob URL is revoked once the popup finishes loading so memory is
 * not held indefinitely.
 */
export function openPrintWindow(html, { width = 1000, height = 1100 } = {}) {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank', `width=${width},height=${height}`);
    if (!w) {
        URL.revokeObjectURL(url);
        // Match the previous UX — a simple alert is fine here because
        // print flows are always user-initiated (button click).
        alert('Please allow pop-ups to print.');
        return null;
    }
    // Revoke the URL once the popup has finished loading the document.
    // Some browsers hold references longer than others, so we also fall
    // back to a delayed revoke as a belt-and-braces safety net.
    const revoke = () => URL.revokeObjectURL(url);
    try {
        w.addEventListener('load', revoke, { once: true });
    } catch { /* cross-context load listener may fail — safe to ignore */ }
    setTimeout(revoke, 60_000);
    return w;
}
